import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { GlassCard, PageHeader } from "../components/shared";
import { FileText, Save, CheckCircle2, ChevronLeft, ChevronRight, UploadCloud } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const SECTIONS = [
    { id: "personal", label: "Personal" },
    { id: "contact", label: "Contact" },
    { id: "academic", label: "Academic" },
    { id: "employment", label: "Employment" },
    { id: "dutyReport", label: "Duty Report" },
    { id: "bankFamily", label: "Bank & Family" },
    { id: "nominee", label: "Nominee" },
    { id: "formF", label: "Form F" },
    { id: "form25", label: "Form 25" },
    { id: "professional", label: "Professional" },
    { id: "uploads", label: "Uploads" }
];

export default function Forms() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("personal");
    const [status, setStatus] = useState("draft"); // "draft" or "submitted"
    const [loading, setLoading] = useState(false);

    const fileInputRef = useRef(null);
    const [currentUploadKey, setCurrentUploadKey] = useState(null);
    const [uploadingKey, setUploadingKey] = useState(null);

    const [formData, setFormData] = useState({
        personal: {
            fullName: "",
            fatherName: "",
            motherName: "",
            dob: "",
            gender: "",
            maritalStatus: "",
            bloodGroup: "",
            nationality: "Indian",
            aadhaar: "",
            pan: "",
            passport: ""
        },
        contact: {
            presentAddress: "",
            permanentAddress: "",
            personalEmail: "",
            emergencyContactName: "",
            emergencyContactRelation: "",
            emergencyContactPhone: ""
        },
        academic: {
            degree: "",
            institute: "",
            passingYear: "",
            score: ""
        },
        employment: {
            previousCompany: "Self Employed",
            jobTitle: "Freelance Developer",
            dates: "2024 - 2026",
            lastSalary: "₹45,000"
        },
        dutyReport: {
            reportingDate: "10/04/2023",
            department: "",
            designation: "",
            manager: "",
            officeLocation: "",
            workingHours: ""
        },
        bankFamily: {
            bankName: "",
            accountNumber: "",
            ifsc: "",
            branch: "",
            familyMemberName: "",
            familyMemberRelation: "",
            familyMemberDob: ""
        },
        nominee: {
            nomineeName: "",
            nomineeRelation: "",
            sharePercentage: "",
            nomineeAadhaar: ""
        },
        formF: {
            gratuityNomineeName: "",
            proportionOfShare: "",
            gratuityNomineeAddress: ""
        },
        form25: {
            epfStatus: "Yes",
            previousPFNumber: "MH/12345/6789",
            uanNumber: "100987654321"
        },
        professional: {
            ref1Name: "",
            ref1Designation: "",
            ref1Phone: "",
            ref2Name: "",
            ref2Designation: "",
            ref2Phone: ""
        },
        uploads: {
            aadhaarFile: "",
            panFile: "",
            academicFile: "",
            experienceFile: ""
        }
    });

    useEffect(() => {
        if (user) {
            // Load from database if exists
            api.get("/employees/me/joining-form")
                .then(({ data }) => {
                    if (data.data && Object.keys(data.data).length > 0) {
                        // Merge with initial template to ensure no fields are missing
                        const merged = { ...formData };
                        Object.keys(data.data).forEach(sec => {
                            merged[sec] = { ...merged[sec], ...data.data[sec] };
                        });
                        setFormData(merged);
                    } else {
                        // Set defaults based on user profile
                        setFormData(prev => ({
                            ...prev,
                            personal: {
                                ...prev.personal,
                                fullName: user.name || "",
                            },
                            contact: {
                                ...prev.contact,
                                personalEmail: user.email || "",
                            },
                            dutyReport: {
                                ...prev.dutyReport,
                                department: user.department || "",
                                designation: user.designation || "",
                                manager: user.reporting_manager || "Unassigned"
                            }
                        }));
                    }
                    if (data.status) {
                        setStatus(data.status);
                    }
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const calculateProgress = (sectionData) => {
        const keys = Object.keys(sectionData);
        if (keys.length === 0) return 0;
        const filled = keys.filter(k => sectionData[k] !== undefined && sectionData[k] !== null && String(sectionData[k]).trim() !== "").length;
        return Math.round((filled / keys.length) * 100);
    };

    const sectionProgress = {
        personal: calculateProgress(formData.personal),
        contact: calculateProgress(formData.contact),
        academic: calculateProgress(formData.academic),
        employment: calculateProgress(formData.employment),
        dutyReport: calculateProgress(formData.dutyReport),
        bankFamily: calculateProgress(formData.bankFamily),
        nominee: calculateProgress(formData.nominee),
        formF: calculateProgress(formData.formF),
        form25: calculateProgress(formData.form25),
        professional: calculateProgress(formData.professional),
        uploads: calculateProgress(formData.uploads)
    };

    const overallProgress = Math.round(
        Object.values(sectionProgress).reduce((a, b) => a + b, 0) / SECTIONS.length
    );

    const handleInputChange = (section, field, value) => {
        if (status === "submitted") return; // locked
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !currentUploadKey) return;
        
        const docTypeMap = {
            aadhaarFile: "aadhaar",
            panFile: "pan",
            academicFile: "other",
            experienceFile: "experience_certificate"
        };
        
        const docType = docTypeMap[currentUploadKey] || "other";
        setUploadingKey(currentUploadKey);
        const tid = toast.loading(`Uploading ${file.name}...`);
        
        try {
            const reader = new FileReader();
            const base64Promise = new Promise((resolve, reject) => {
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
            });
            reader.readAsDataURL(file);
            const base64 = await base64Promise;
            const base64Content = base64.split(",")[1];
            
            const { data } = await api.post("/documents/upload", {
                employee_id: user.id || user._id,
                name: file.name,
                doc_type: docType,
                content: base64Content
            });
            
            handleInputChange("uploads", currentUploadKey, data.file_url);
            toast.success("Document uploaded successfully!", { id: tid });
        } catch (err) {
            toast.error(formatApiError(err), { id: tid });
        } finally {
            setUploadingKey(null);
            setCurrentUploadKey(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const saveDraft = async (silent = false) => {
        if (status === "submitted") return;
        setLoading(true);
        try {
            await api.patch("/employees/me/joining-form", {
                data: formData,
                status: "draft"
            });
            if (!silent) toast.success("Draft onboarding form saved successfully!");
        } catch (e) {
            if (!silent) toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const finalSubmit = async () => {
        if (status === "submitted") return;
        
        // Basic validation: ensure required Personal/Contact fields are filled
        if (!formData.personal.fullName || !formData.personal.dob || !formData.personal.gender || !formData.personal.aadhaar) {
            toast.error("Please fill out required fields in Personal section (Name, DOB, Gender, Aadhaar) before submitting.");
            setActiveTab("personal");
            return;
        }

        if (!window.confirm("Are you sure you want to final submit? This will lock all details and notify HR.")) return;
        setLoading(true);
        try {
            await api.patch("/employees/me/joining-form", {
                data: formData,
                status: "submitted"
            });
            setStatus("submitted");
            toast.success("Onboarding formalities completed successfully! Form is now locked.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const navigateTab = (direction) => {
        const currentIndex = SECTIONS.findIndex(s => s.id === activeTab);
        if (direction === "next" && currentIndex < SECTIONS.length - 1) {
            setActiveTab(SECTIONS[currentIndex + 1].id);
        } else if (direction === "prev" && currentIndex > 0) {
            setActiveTab(SECTIONS[currentIndex - 1].id);
        }
    };

    const getAvatarBg = (name) => {
        const colors = ["bg-teal-600", "bg-blue-600", "bg-pink-600", "bg-emerald-600"];
        let hash = 0;
        for (let i = 0; i < (name?.length || 0); i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const getInitials = (name) => {
        if (!name) return "EE";
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    };

    // Render forms inside sections
    const renderActiveForm = () => {
        const isLocked = status === "submitted";
        const inputClass = "mt-1 text-xs";

        switch (activeTab) {
            case "personal":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Full Name *</Label>
                            <Input disabled={isLocked} value={formData.personal.fullName} onChange={e => handleInputChange("personal", "fullName", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Father Name *</Label>
                            <Input disabled={isLocked} value={formData.personal.fatherName} onChange={e => handleInputChange("personal", "fatherName", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Mother Name *</Label>
                            <Input disabled={isLocked} value={formData.personal.motherName} onChange={e => handleInputChange("personal", "motherName", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Date of Birth *</Label>
                            <Input disabled={isLocked} type="date" value={formData.personal.dob} onChange={e => handleInputChange("personal", "dob", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Gender *</Label>
                            <select disabled={isLocked} value={formData.personal.gender} onChange={e => handleInputChange("personal", "gender", e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-xs">
                                <option value="">Select</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Marital Status *</Label>
                            <select disabled={isLocked} value={formData.personal.maritalStatus} onChange={e => handleInputChange("personal", "maritalStatus", e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-xs">
                                <option value="">Select</option>
                                <option value="single">Single</option>
                                <option value="married">Married</option>
                                <option value="relationship">In a Relationship</option>
                                <option value="engaged">Engaged</option>
                                <option value="divorced">Divorced</option>
                                <option value="widowed">Widowed</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Blood Group *</Label>
                            <select disabled={isLocked} value={formData.personal.bloodGroup} onChange={e => handleInputChange("personal", "bloodGroup", e.target.value)} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-xs">
                                <option value="">Select</option>
                                <option value="A+">A+</option>
                                <option value="A-">A-</option>
                                <option value="B+">B+</option>
                                <option value="B-">B-</option>
                                <option value="AB+">AB+</option>
                                <option value="AB-">AB-</option>
                                <option value="O+">O+</option>
                                <option value="O-">O-</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Nationality *</Label>
                            <Input disabled={isLocked} value={formData.personal.nationality} onChange={e => handleInputChange("personal", "nationality", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Aadhaar Number *</Label>
                            <Input disabled={isLocked} value={formData.personal.aadhaar} onChange={e => handleInputChange("personal", "aadhaar", e.target.value)} className={inputClass} placeholder="12-digit UID" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">PAN Number *</Label>
                            <Input disabled={isLocked} value={formData.personal.pan} onChange={e => handleInputChange("personal", "pan", e.target.value)} className={inputClass} placeholder="10-digit PAN" />
                        </div>
                        <div className="col-span-1 md:col-span-2">
                            <Label className="text-xs font-semibold text-gray-700">Passport Number</Label>
                            <Input disabled={isLocked} value={formData.personal.passport} onChange={e => handleInputChange("personal", "passport", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                );

            case "contact":
                return (
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Present Address *</Label>
                            <textarea disabled={isLocked} value={formData.contact.presentAddress} onChange={e => handleInputChange("contact", "presentAddress", e.target.value)} className="mt-1 w-full min-h-[60px] rounded-xl border border-input bg-background px-3 py-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Permanent Address *</Label>
                            <textarea disabled={isLocked} value={formData.contact.permanentAddress} onChange={e => handleInputChange("contact", "permanentAddress", e.target.value)} className="mt-1 w-full min-h-[60px] rounded-xl border border-input bg-background px-3 py-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Personal Email *</Label>
                                <Input disabled={isLocked} type="email" value={formData.contact.personalEmail} onChange={e => handleInputChange("contact", "personalEmail", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Emergency Contact Person *</Label>
                                <Input disabled={isLocked} value={formData.contact.emergencyContactName} onChange={e => handleInputChange("contact", "emergencyContactName", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Relationship *</Label>
                                <Input disabled={isLocked} value={formData.contact.emergencyContactRelation} onChange={e => handleInputChange("contact", "emergencyContactRelation", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Emergency Phone *</Label>
                                <Input disabled={isLocked} value={formData.contact.emergencyContactPhone} onChange={e => handleInputChange("contact", "emergencyContactPhone", e.target.value)} className={inputClass} />
                            </div>
                        </div>
                    </div>
                );

            case "academic":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Highest Degree/Qualification *</Label>
                            <Input disabled={isLocked} value={formData.academic.degree} onChange={e => handleInputChange("academic", "degree", e.target.value)} className={inputClass} placeholder="e.g. B.Tech (CS)" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Institute / University *</Label>
                            <Input disabled={isLocked} value={formData.academic.institute} onChange={e => handleInputChange("academic", "institute", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Year of Passing *</Label>
                            <Input disabled={isLocked} type="number" value={formData.academic.passingYear} onChange={e => handleInputChange("academic", "passingYear", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Percentage / CGPA *</Label>
                            <Input disabled={isLocked} value={formData.academic.score} onChange={e => handleInputChange("academic", "score", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                );

            case "employment":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Previous Company</Label>
                            <Input disabled={isLocked} value={formData.employment.previousCompany} onChange={e => handleInputChange("employment", "previousCompany", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Previous Designation</Label>
                            <Input disabled={isLocked} value={formData.employment.jobTitle} onChange={e => handleInputChange("employment", "jobTitle", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Tenure/Dates</Label>
                            <Input disabled={isLocked} value={formData.employment.dates} onChange={e => handleInputChange("employment", "dates", e.target.value)} className={inputClass} placeholder="e.g. 2024 - 2026" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Last Drawn Annual Salary</Label>
                            <Input disabled={isLocked} value={formData.employment.lastSalary} onChange={e => handleInputChange("employment", "lastSalary", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                );

            case "dutyReport":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Reporting Date</Label>
                            <Input disabled value={formData.dutyReport.reportingDate} className={`${inputClass} bg-gray-50 cursor-not-allowed`} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Allocated Department</Label>
                            <Input disabled value={formData.dutyReport.department} className={`${inputClass} bg-gray-50 cursor-not-allowed`} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Allocated Designation</Label>
                            <Input disabled value={formData.dutyReport.designation} className={`${inputClass} bg-gray-50 cursor-not-allowed`} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Reporting Manager / Team Lead</Label>
                            <Input disabled value={formData.dutyReport.manager} className={`${inputClass} bg-gray-50 cursor-not-allowed`} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Office Location</Label>
                            <Input disabled={isLocked} value={formData.dutyReport.officeLocation} onChange={e => handleInputChange("dutyReport", "officeLocation", e.target.value)} className={inputClass} placeholder="e.g. Head Office Udaipur" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Shift/Working Hours</Label>
                            <Input disabled={isLocked} value={formData.dutyReport.workingHours} onChange={e => handleInputChange("dutyReport", "workingHours", e.target.value)} className={inputClass} placeholder="e.g. 10:00 AM - 06:00 PM" />
                        </div>
                    </div>
                );

            case "bankFamily":
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Bank Name *</Label>
                                <Input disabled={isLocked} value={formData.bankFamily.bankName} onChange={e => handleInputChange("bankFamily", "bankName", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Bank Account Number *</Label>
                                <Input disabled={isLocked} value={formData.bankFamily.accountNumber} onChange={e => handleInputChange("bankFamily", "accountNumber", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Bank IFSC Code *</Label>
                                <Input disabled={isLocked} value={formData.bankFamily.ifsc} onChange={e => handleInputChange("bankFamily", "ifsc", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Branch Name *</Label>
                                <Input disabled={isLocked} value={formData.bankFamily.branch} onChange={e => handleInputChange("bankFamily", "branch", e.target.value)} className={inputClass} />
                            </div>
                        </div>
                        <div className="border-t pt-4">
                            <h4 className="font-heading text-xs font-bold text-slate-800 dark:text-slate-200 mb-3">Family Detail</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Family Member Name *</Label>
                                    <Input disabled={isLocked} value={formData.bankFamily.familyMemberName} onChange={e => handleInputChange("bankFamily", "familyMemberName", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Relationship *</Label>
                                    <Input disabled={isLocked} value={formData.bankFamily.familyMemberRelation} onChange={e => handleInputChange("bankFamily", "familyMemberRelation", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Date of Birth *</Label>
                                    <Input disabled={isLocked} type="date" value={formData.bankFamily.familyMemberDob} onChange={e => handleInputChange("bankFamily", "familyMemberDob", e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "nominee":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Nominee Name *</Label>
                            <Input disabled={isLocked} value={formData.nominee.nomineeName} onChange={e => handleInputChange("nominee", "nomineeName", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Nominee Relationship *</Label>
                            <Input disabled={isLocked} value={formData.nominee.nomineeRelation} onChange={e => handleInputChange("nominee", "nomineeRelation", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Proportion of Share (%) *</Label>
                            <Input disabled={isLocked} type="number" value={formData.nominee.sharePercentage} onChange={e => handleInputChange("nominee", "sharePercentage", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Nominee Aadhaar UID *</Label>
                            <Input disabled={isLocked} value={formData.nominee.nomineeAadhaar} onChange={e => handleInputChange("nominee", "nomineeAadhaar", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                );

            case "formF":
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Gratuity Nominee Name *</Label>
                                <Input disabled={isLocked} value={formData.formF.gratuityNomineeName} onChange={e => handleInputChange("formF", "gratuityNomineeName", e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Proportion of Share Interest *</Label>
                                <Input disabled={isLocked} value={formData.formF.proportionOfShare} onChange={e => handleInputChange("formF", "proportionOfShare", e.target.value)} className={inputClass} placeholder="e.g. 100%" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Full Address of Nominee *</Label>
                            <textarea disabled={isLocked} value={formData.formF.gratuityNomineeAddress} onChange={e => handleInputChange("formF", "gratuityNomineeAddress", e.target.value)} className="mt-1 w-full min-h-[60px] rounded-xl border border-input bg-background px-3 py-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                    </div>
                );

            case "form25":
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">EPF Member Status *</Label>
                            <Input disabled={isLocked} value={formData.form25.epfStatus} onChange={e => handleInputChange("form25", "epfStatus", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Previous PF Number</Label>
                            <Input disabled={isLocked} value={formData.form25.previousPFNumber} onChange={e => handleInputChange("form25", "previousPFNumber", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">UAN Number</Label>
                            <Input disabled={isLocked} value={formData.form25.uanNumber} onChange={e => handleInputChange("form25", "uanNumber", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                );

            case "professional":
                return (
                    <div className="space-y-4">
                        <div>
                            <h4 className="font-heading text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">Reference 1</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Name *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref1Name} onChange={e => handleInputChange("professional", "ref1Name", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Designation *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref1Designation} onChange={e => handleInputChange("professional", "ref1Designation", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Phone *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref1Phone} onChange={e => handleInputChange("professional", "ref1Phone", e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        </div>
                        <div className="border-t pt-4">
                            <h4 className="font-heading text-xs font-bold text-slate-800 dark:text-slate-200 mb-2">Reference 2</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Name *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref2Name} onChange={e => handleInputChange("professional", "ref2Name", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Designation *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref2Designation} onChange={e => handleInputChange("professional", "ref2Designation", e.target.value)} className={inputClass} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-gray-700">Phone *</Label>
                                    <Input disabled={isLocked} value={formData.professional.ref2Phone} onChange={e => handleInputChange("professional", "ref2Phone", e.target.value)} className={inputClass} />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case "uploads":
                const renderUploadSlot = (label, key) => (
                    <div className="border border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center bg-slate-50/50 dark:bg-slate-900/10">
                        <UploadCloud className="w-8 h-8 text-teal-600 mb-2" />
                        <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{label}</span>
                        {formData.uploads[key] ? (
                            <a 
                                href={formData.uploads[key]} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[10px] text-emerald-600 hover:text-emerald-700 underline font-bold mt-1 uppercase block animate-pulse"
                            >
                                Uploaded ✓ (View)
                            </a>
                        ) : (
                            <span className="text-[10px] text-muted-foreground mt-1">Accepts PDF, PNG, JPG</span>
                        )}
                        {!isLocked && (
                            <Button 
                                type="button" 
                                size="sm" 
                                variant="outline" 
                                className="mt-3 bg-white border text-black hover:bg-gray-50 h-7 text-[10px] rounded-lg"
                                onClick={() => {
                                    setCurrentUploadKey(key);
                                    if (fileInputRef.current) {
                                        fileInputRef.current.click();
                                    }
                                }}
                                disabled={uploadingKey === key}
                            >
                                {uploadingKey === key ? "Uploading..." : "Select File"}
                            </Button>
                        )}
                    </div>
                );
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderUploadSlot("Aadhaar Card copy *", "aadhaarFile")}
                        {renderUploadSlot("PAN Card copy *", "panFile")}
                        {renderUploadSlot("Graduation Certificate *", "academicFile")}
                        {renderUploadSlot("Previous Experience Letter", "experienceFile")}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div>
            <PageHeader title="Forms" subtitle="Complete your MNC job joining formality onboarding forms." />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left Card: Summary, Progress & Tabs List */}
                <div className="lg:col-span-1 space-y-4">
                    <GlassCard className="p-5 flex flex-col border border-teal-100/50 dark:border-teal-900/50">
                        <div className="flex items-center gap-3 border-b pb-4">
                            <div className={`w-10 h-10 rounded-full ${getAvatarBg(user?.name)} text-white flex items-center justify-center font-bold text-sm uppercase shadow-sm`}>
                                {getInitials(user?.name)}
                            </div>
                            <div className="min-w-0">
                                <h4 className="font-heading font-bold text-sm text-slate-800 dark:text-slate-100 truncate">{user?.name}</h4>
                                <p className="text-[10px] font-semibold text-muted-foreground font-mono truncate">{user?.employee_id}</p>
                            </div>
                        </div>

                        <div className="mt-4">
                            <div className="flex justify-between items-center text-xs font-semibold mb-1.5 text-slate-700 dark:text-slate-300">
                                <span>Draft Saved progress</span>
                                <span>{overallProgress}%</span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                <div className="bg-teal-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${overallProgress}%` }} />
                            </div>
                        </div>

                        <div className="mt-4">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border
                                ${status === "submitted" 
                                    ? "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/20 dark:text-rose-400"
                                    : "bg-teal-50 text-teal-600 border-teal-200 dark:bg-teal-950/20 dark:text-teal-400"
                                }`}>
                                {status === "submitted" ? "Locked" : "Draft"}
                            </span>
                        </div>
                    </GlassCard>

                    {/* Navigation Sidebar List */}
                    <GlassCard className="p-2 border border-teal-100/50 dark:border-teal-900/50">
                        <nav className="space-y-0.5">
                            {SECTIONS.map((sec) => {
                                const active = activeTab === sec.id;
                                const progress = sectionProgress[sec.id] || 0;
                                return (
                                    <button
                                        key={sec.id}
                                        onClick={() => setActiveTab(sec.id)}
                                        className={`w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-semibold rounded-xl transition-all
                                            ${active 
                                                ? "bg-teal-50 text-teal-600 dark:bg-teal-950/30 dark:text-teal-400"
                                                : "text-foreground/70 hover:bg-black/5 dark:hover:bg-white/5"
                                            }`}
                                    >
                                        <span>{sec.label}</span>
                                        <span className={active ? "text-teal-600 font-bold" : "text-muted-foreground font-medium"}>
                                            {progress}%
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>
                    </GlassCard>
                </div>

                {/* Right Card: Active Tab Form Fields */}
                <GlassCard className="lg:col-span-3 p-6 flex flex-col justify-between min-h-[480px] border border-teal-100/50 dark:border-teal-900/50">
                    <div>
                        <div className="flex justify-between items-center mb-4 pb-3 border-b border-black/5 dark:border-white/5">
                            <div>
                                <h3 className="font-heading font-bold text-base text-slate-800 dark:text-slate-100 capitalize">
                                    {SECTIONS.find(s => s.id === activeTab)?.label} Details
                                </h3>
                                <p className="text-[10px] text-muted-foreground">Save draft anytime and resume later.</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase
                                ${status === "submitted" 
                                    ? "bg-slate-100 text-slate-500 border-slate-200"
                                    : "bg-emerald-50 text-emerald-600 border-emerald-200"
                                }`}>
                                {status === "submitted" ? "Locked" : "Editable"}
                            </span>
                        </div>

                        <div className="py-2">
                            {renderActiveForm()}
                        </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-black/5 dark:border-white/5 pt-4 mt-6">
                        <div className="flex gap-2">
                            <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                className="bg-white border text-black hover:bg-gray-50 h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-semibold disabled:opacity-50"
                                onClick={() => navigateTab("prev")}
                                disabled={activeTab === SECTIONS[0].id}
                            >
                                <ChevronLeft className="w-3.5 h-3.5" /> Previous
                            </Button>
                            <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                className="bg-white border text-black hover:bg-gray-50 h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-semibold disabled:opacity-50"
                                onClick={() => navigateTab("next")}
                                disabled={activeTab === SECTIONS[SECTIONS.length - 1].id}
                            >
                                Next <ChevronRight className="w-3.5 h-3.5" />
                            </Button>
                        </div>

                        {status !== "submitted" && (
                            <div className="flex gap-2">
                                <Button 
                                    type="button" 
                                    size="sm" 
                                    variant="outline" 
                                    className="bg-white border border-teal-200 hover:bg-teal-50/50 text-teal-600 h-9 px-3 rounded-lg flex items-center gap-1.5 text-xs font-semibold"
                                    onClick={() => saveDraft(false)}
                                    disabled={loading}
                                >
                                    <Save className="w-3.5 h-3.5" /> Save Draft
                                </Button>
                                <Button 
                                    type="button" 
                                    size="sm" 
                                    className="bg-teal-600 hover:bg-teal-700 text-white h-9 px-3.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold"
                                    onClick={finalSubmit}
                                    disabled={loading}
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Final Submit
                                </Button>
                            </div>
                        )}
                    </div>
                </GlassCard>
            </div>

            {/* Hidden File Input for Real Uploads */}
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                onChange={handleFileChange}
                accept=".pdf,.png,.jpg,.jpeg,.webp"
            />
        </div>
    );
}
